import { useMemo } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import type { RcaSubGraph, RcaGraphNode } from '../../api/client'

// ── Node visual vocabulary ────────────────────────────────────────────────────
const TYPE_ICON: Record<RcaGraphNode['type'], string> = {
  service: '🧩',
  pod: '📦',
  signal: '📡',
  cause: '⚠️',
  alert: '🔔',
  remediation: '🛠️',
}

const TYPE_LABEL: Record<RcaGraphNode['type'], string> = {
  service: 'Service',
  pod: 'Pod',
  signal: 'Signal',
  cause: 'Cause',
  alert: 'Alert',
  remediation: 'Fix',
}

// Status → border / background. Culprit and impacted read "red" like the reference UI.
function statusStyle(status: RcaGraphNode['status']): { border: string; background: string } {
  switch (status) {
    case 'culprit':
      return { border: 'var(--error)', background: 'rgba(var(--error-rgb, 220, 80, 80), 0.10)' }
    case 'impacted':
      return { border: 'var(--error)', background: 'rgba(var(--error-rgb, 220, 80, 80), 0.05)' }
    case 'contributing':
      return { border: 'var(--warning)', background: 'var(--bg-card)' }
    default:
      return { border: 'var(--border)', background: 'var(--bg-card)' }
  }
}

const NODE_W = 196
const NODE_H = 60
const X_GAP = 248
const Y_GAP = 84

type RcaNodeData = RcaGraphNode & Record<string, unknown>

function RcaNode({ data }: NodeProps<Node<RcaNodeData>>) {
  const { border, background } = statusStyle(data.status)
  return (
    <div
      title={data.detail || data.label}
      style={{
        width: NODE_W,
        minHeight: NODE_H,
        boxSizing: 'border-box',
        border: `1.5px solid ${border}`,
        borderRadius: 8,
        background,
        padding: '8px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 13 }}>{TYPE_ICON[data.type] ?? '•'}</span>
        <span
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: 'var(--text-h)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {data.label}
        </span>
      </div>
      <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--mono)' }}>
        {data.detail || TYPE_LABEL[data.type]}
      </span>
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </div>
  )
}

const nodeTypes = { rca: RcaNode }

// ── Layered left→right layout ─────────────────────────────────────────────────
// Depth = longest path from any root (in-degree 0). Nodes at the same depth stack
// vertically. Handles linear causal chains and hub-and-spoke impact graphs alike.
function layout(graph: RcaSubGraph): { nodes: Node<RcaNodeData>[]; edges: Edge[] } {
  const ids = graph.nodes.map((n) => n.id)
  const idSet = new Set(ids)
  const validEdges = graph.edges.filter((e) => idSet.has(e.source) && idSet.has(e.target))

  const indegree = new Map<string, number>(ids.map((id) => [id, 0]))
  const adj = new Map<string, string[]>(ids.map((id) => [id, []]))
  for (const e of validEdges) {
    indegree.set(e.target, (indegree.get(e.target) ?? 0) + 1)
    adj.get(e.source)?.push(e.target)
  }

  // BFS depth from roots; fall back so disconnected nodes still get a depth.
  const depth = new Map<string, number>()
  const queue = ids.filter((id) => (indegree.get(id) ?? 0) === 0)
  for (const id of queue) depth.set(id, 0)
  let head = 0
  while (head < queue.length) {
    const cur = queue[head++]
    const d = depth.get(cur) ?? 0
    for (const next of adj.get(cur) ?? []) {
      const nd = Math.max(depth.get(next) ?? 0, d + 1)
      if (!depth.has(next) || nd > (depth.get(next) ?? 0)) {
        depth.set(next, nd)
        queue.push(next)
      }
    }
  }
  for (const id of ids) if (!depth.has(id)) depth.set(id, 0)

  const perDepth = new Map<number, number>()
  const nodes: Node<RcaNodeData>[] = graph.nodes.map((n) => {
    const d = depth.get(n.id) ?? 0
    const row = perDepth.get(d) ?? 0
    perDepth.set(d, row + 1)
    return {
      id: n.id,
      type: 'rca',
      position: { x: d * X_GAP, y: row * Y_GAP },
      data: { ...n },
    }
  })

  const edges: Edge[] = validEdges.map((e, i) => ({
    id: `e${i}-${e.source}-${e.target}`,
    source: e.source,
    target: e.target,
    label: e.label || undefined,
    animated: true,
    style: { stroke: 'var(--text-muted)', strokeWidth: 1.5 },
    labelStyle: { fontSize: 10, fill: 'var(--text-muted)' },
    labelBgStyle: { fill: 'var(--bg-soft)' },
  }))

  return { nodes, edges }
}

export default function LineageGraph({
  graph,
  height = 280,
  ariaLabel,
}: {
  graph: RcaSubGraph
  height?: number
  ariaLabel?: string
}) {
  const { nodes, edges } = useMemo(() => layout(graph), [graph])

  if (nodes.length === 0) return null

  return (
    <div
      aria-label={ariaLabel}
      style={{
        height,
        width: '100%',
        border: '1px solid var(--border)',
        borderRadius: 8,
        background: 'var(--bg-soft)',
        overflow: 'hidden',
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        minZoom={0.2}
        maxZoom={1.5}
      >
        <Background gap={20} color="var(--border)" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  )
}
